import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { useNavigate } from 'react-router-dom';

interface LoginCredentials {
    email: string;
    password: string;
}

interface RegisterCredentials extends LoginCredentials {
    name: string;
    type: string;
}

interface User {
    id: number;
    name: string;
    email: string;
    type: 'USER' | 'CRM' | 'ADMIN';
}

export const useLogin = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation<ApiResponse<User>, Error, LoginCredentials>({
        mutationFn: async credentials => {
            const { data } = await httpClient.post<ApiResponse<User>>(
                API_ENDPOINTS.USERS.LOGIN,
                credentials
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
            navigate('/');
        },
    });
};

export const useLogout = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error>({
        mutationFn: async () => {
            await httpClient.post(API_ENDPOINTS.USERS.LOGOUT);
        },
        onSuccess: () => {
            queryClient.clear();
        },
    });
};

export const useRegister = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<User>, Error, RegisterCredentials>({
        mutationFn: async credentials => {
            const { data } = await httpClient.post<ApiResponse<User>>(
                API_ENDPOINTS.USERS.REGISTER,
                credentials
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
        },
    });
};

export const useGetUserProfile = (options?: { enabled?: boolean }) => {
    const nullResponse = {
        data: null,
    } as ApiResponse<null>;

    return useQuery<ApiResponse<User | null>>({
        queryKey: ['user-profile'],
        queryFn: async () => {
            try {
                const { data, status } = await httpClient.get<ApiResponse<User>>(
                    API_ENDPOINTS.USERS.PROFILE
                );
                if (status === 401) {
                    return nullResponse;
                }
                return data;
            } catch (error) {
                return nullResponse;
            }
        },
        enabled: options?.enabled !== false,
    });
};

export const useForgotPassword = () => {
    return useMutation<ApiResponse<void>, Error, { email: string }>({
        mutationFn: async ({ email }) => {
            // Detect user type from hostname
            const isCRMPortal =
                window.location.hostname.includes('admin') ||
                window.location.hostname.includes('crm');
            const endpoint = isCRMPortal
                ? API_ENDPOINTS.USERS.FORGOT_PASSWORD
                : API_ENDPOINTS.CUSTOMERS.FORGOT_PASSWORD;

            const { data } = await httpClient.post<ApiResponse<void>>(endpoint, { email });
            return data;
        },
    });
};

export const useResetPassword = () => {
    return useMutation<
        ApiResponse<void>,
        Error,
        {
            email: string;
            password: string;
            password_confirmation: string;
            token: string;
        }
    >({
        mutationFn: async resetData => {
            // Detect user type from hostname
            const isCRMPortal =
                window.location.hostname.includes('admin') ||
                window.location.hostname.includes('crm');
            const endpoint = isCRMPortal
                ? API_ENDPOINTS.USERS.RESET_PASSWORD
                : API_ENDPOINTS.CUSTOMERS.RESET_PASSWORD;

            const { data } = await httpClient.post<ApiResponse<void>>(endpoint, resetData);
            return data;
        },
    });
};

export const useVerifyEmail = () => {
    return useMutation<ApiResponse<void>, Error, { token: string; email: string }>({
        mutationFn: async ({ token, email }) => {
            const { data } = await httpClient.post<ApiResponse<void>>(
                API_ENDPOINTS.USERS.VERIFY_EMAIL,
                { token, email }
            );
            return data;
        },
    });
};

export const useResendVerification = () => {
    return useMutation<ApiResponse<void>, Error, { email: string }>({
        mutationFn: async ({ email }) => {
            const { data } = await httpClient.post<ApiResponse<void>>(
                API_ENDPOINTS.USERS.RESEND_VERIFICATION,
                { email }
            );
            return data;
        },
    });
};
